import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import * as services from "../../services/saleng";
import { formatDate } from "../../utils/formatDate";
import SnapshotItemsDialog from "./SnapshotItemsDialog";
import toast from "react-hot-toast";

export default function SnapshotDialog({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (open) fetchSnapshots();
  }, [open]);

  const fetchSnapshots = async () => {
    try {
      setLoading(true);
      const res = await services.getSnapshots();
      const data = res.data.data || [];

      const mappedData = data.map((item, idx) => ({
        id: item.id,
        no: idx + 1,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        note: item.note,
        status: item.status,
        items: item.items || [],
      }));

      setRows(mappedData);
    } catch (err) {
      console.error("Error fetching snapshot:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewItems = (items) => {
    setSelectedItems(items);
    setItemsDialogOpen(true);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      setLoading(true);
      const newStatus = currentStatus === "published" ? "draft" : "published";
      await services.updateSnapshotStatus(id, newStatus);
      toast.success(
        `อัปเดตสถานะเป็น ${newStatus === "published" ? "ใช้งาน" : "ร่าง"} เรียบร้อย!`
      );
      fetchSnapshots(); // โหลดข้อมูลใหม่
    } catch (err) {
      toast.error("อัปเดตสถานะไม่สำเร็จ!");
      console.error("Error updating snapshot status:", err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "no", headerName: "ที่", width: 70 },
    {
      field: "createdAt",
      headerName: "วันที่บันทึก",
      flex: 1.5,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "status",
      headerName: "สถานะ",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value === "published" ? "ประกาศราคาแล้ว" : "ร่าง"}
          color={params.value === "published" ? "success" : "default"}
        />
      ),
    },
    {
      field: "updatedAt",
      headerName: "ประกาศใช้เมื่อ",
      flex: 1.5,
      renderCell: (params) => {
        if (params.row.status === "draft") return null;
        return formatDate(params.value);
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "จัดการ",
      flex: 1.5,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            key="view-items"
            label="ดู"
            showInMenu
            onClick={() => handleViewItems(params.row.items)}
          />,
        ];

        // ปุ่ม toggle status
        actions.push(
          <GridActionsCellItem
            key="toggle-status"
            label={
              params.row.status === "published" ? "ยกเลิกใช้งาน" : "ใช้งาน"
            }
            showInMenu
            onClick={() => handleToggleStatus(params.row.id, params.row.status)}
          />
        );

        return actions;
      },
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>บันทึกราคาประกาศ</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              pageSizeOptions={[10]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained" color="primary">
            ปิด
          </Button>
        </DialogActions>
      </Dialog>

      <SnapshotItemsDialog
        open={itemsDialogOpen}
        onClose={() => setItemsDialogOpen(false)}
        items={selectedItems}
      />
    </>
  );
}
